import React, {startTransition, useState} from "react";
import {useQueryClient} from "react-query";
import {useNavigate} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import {tokenSelector, userSelector} from "../../Recoil/Atom/authState";
import {useDepartmentList} from "../../hooks/useDepartmentList";
import {Controller, useForm} from "react-hook-form";
import SubTitle from "../../components/SubTitle";
import {FilterGroup, SubMenuGroup} from "../../styledComponents/StyledListContainer";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  styled,
} from "@mui/material";
import {locationValue} from "../../models/clientModels/data/selectList";
import {
  dangerEvaluationRevisionListActionButton,
  evaluationMasterListActionButton,
} from "../../models/clientModels/data/actionButtonData";
import ActionButtonGroup from "../../components/ActionButtonGroup";
import {PaginationContainer} from "../../styledComponents/StyledPagination";
import MWSkeleton from "../../components/MWSkeleton";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {
  EvaluationListParams,
  initializeEvaluationListParams,
} from "../../models/serverModels/permission/WPDangerEvaluation";

import {WPEvaluationMaster} from "../../models/serverModels/permission/WPEvaluationMaster";
import {
  ResponseItem,
  ResponseList,
  ResponseRemove,
} from "../../models/clientModels/interfaces/Response";
import * as evaluationMasterApi from "../../api/permissionApi/evaluationMasterApi";
import * as Params from "../../models/clientModels/interfaces/apiParams";

import {useCopyMutation, useGetQuery, useRemoveMutation} from "../../hooks/useQueryHooks";
import {alertStateAtom} from "../../Recoil/Atom/AlertAtom";
import {ReactComponent as WordIcon} from "../../assets/images/word.svg";
import {Link as RouterLink} from "react-router-dom";
import {snackBarState} from "../../Recoil/Atom/snackBarState";
import {dangerEvaluationFilterAtom} from "../../Recoil/Atom/filterState";
import {StyledTableContainer, StyledTableHead} from "../../styledComponents/StyledTable";
import ko from "date-fns/locale/ko";

const ListContainer = styled(Box)(({theme}) => ({
  minWidth: "1500px",
}));

const EvaluationMasterList = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const tokenState = useRecoilValue(tokenSelector);
  const setAlertState = useSetRecoilState(alertStateAtom);
  const setSnackBarState = useSetRecoilState(snackBarState);
  const userInfo = useRecoilValue(userSelector);

  const [page, setPage] = useState<number>(1);

  // const [filter, setFilter] = useState<EvaluationListParams>(initializeEvaluationListParams());
  const [filter, setFilter] = useRecoilState(dangerEvaluationFilterAtom);

  const queryString = Object.entries(filter)
    .map((e) => e.join("="))
    .join("&");

  const {departmentList} = useDepartmentList();

  const {control, reset, setValue} = useForm();

  const {
    data: evaluationList,
    isLoading,
    isFetching,
  } = useGetQuery<ResponseList<WPEvaluationMaster>>({
    key: ["EvaluationMasterList", {...filter, token: tokenState}],
    api: () => evaluationMasterApi.getList({...filter, token: tokenState}),
  });

  const {mutate: removeMutate} = useRemoveMutation<ResponseRemove, Params.Remove>({
    api: evaluationMasterApi.remove,
    options: {
      onSuccess: () => {
        setSnackBarState("delete");
        queryClient.invalidateQueries("EvaluationMasterList");
      },
    },
  });

  const FilteringHandler = (
    event: React.BaseSyntheticEvent<Event, EventTarget & HTMLInputElement>
  ) => {
    setValue(event.target.name, event.target.value);
    setFilter({...filter, page: 1, [event.target.name]: event.target.value});
  };

  const selectChangeHandler = (event: SelectChangeEvent<any>) => {
    startTransition(() => {
      setValue(event.target.name, event.target?.value);
      setFilter({...filter, page: 1, [event.target.name]: event.target?.value});
    });
  };

  const formatDate = (date: Date | null) => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const startDateHandler = (newValue: Date | null) => {
    const formattedDate = formatDate(newValue);
    setFilter({
      ...filter,
      page: 1,
      startDate: formattedDate,
    });
  };

  const endDateHandler = (newValue: Date | null) => {
    const formattedDate = formatDate(newValue);
    setFilter({
      ...filter,
      page: 1,
      endDate: formattedDate,
    });
  };

  const resetHandler = () => {
    setFilter(initializeEvaluationListParams());
    reset();
  };

  const paginationHandleChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setFilter({...filter, page: value});
    setPage(value);
  };

  const {mutate: copyMutate} = useCopyMutation<ResponseItem<WPEvaluationMaster>, Params.Copy>({
    api: evaluationMasterApi.copy,
    options: {
      onSuccess: () => {
        setSnackBarState("copy");
        queryClient.invalidateQueries("EvaluationMasterList");
      },
    },
  });

  return (
    <ListContainer>
      <SubTitle subTitle="작업위험성평가 리스트" />
      <SubMenuGroup>
        <FilterGroup>
          <Box>
            <Controller
              name="range"
              control={control}
              defaultValue={-1}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="range">범위</InputLabel>
                  <Select
                    style={{backgroundColor: "#fff"}}
                    {...field}
                    labelId="range"
                    id="range"
                    label="범위"
                    size="small"
                    value={filter?.range}
                    onChange={selectChangeHandler}
                  >
                    {locationValue?.map((item) => (
                      <MenuItem
                        style={{backgroundColor: "#fff"}}
                        key={item.value}
                        value={item?.value!}
                      >
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <Box>
            <Controller
              name="departmentUID"
              control={control}
              defaultValue={-1}
              render={({field}) => (
                <FormControl>
                  <InputLabel id="departmentUID">작성부서</InputLabel>
                  <Select
                    style={{backgroundColor: "#fff"}}
                    {...field}
                    labelId="departmentUID"
                    id="departmentUID"
                    label="작성부서"
                    size="small"
                    value={filter?.departmentUID}
                    onChange={selectChangeHandler}
                  >
                    <MenuItem value={-1}>전체</MenuItem>
                    {departmentList?.items.map((item) => (
                      <MenuItem style={{backgroundColor: "#fff"}} key={item.uid} value={item?.uid}>
                        {item.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Box style={{backgroundColor: "#fff"}}>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="시작일"
                value={filter.startDate}
                onChange={startDateHandler}
                renderInput={(params) => (
                  <TextField size="small" sx={{fontSize: "10px"}} {...params} />
                )}
              />
            </Box>
            <Box style={{backgroundColor: "#fff"}}>
              <DatePicker
                inputFormat={"yyyy년MM월dd일"}
                label="종료일"
                value={filter.endDate}
                onChange={endDateHandler}
                renderInput={(params) => <TextField size="small" {...params} />}
              />
            </Box>
          </LocalizationProvider>
          <Box>
            <Controller
              name="contents"
              control={control}
              render={({field}) => (
                <TextField
                  style={{backgroundColor: "#fff"}}
                  {...field}
                  label="내용"
                  size="small"
                  value={filter.contents}
                  onChange={FilteringHandler}
                />
              )}
            />
          </Box>

          <Button
            onClick={resetHandler}
            variant="contained"
            color="success"
            size="small"
            sx={{whiteSpace: "nowrap"}}
          >
            초기화
          </Button>
        </FilterGroup>
        <ActionButtonGroup data={evaluationMasterListActionButton(queryString)} />
      </SubMenuGroup>
      <StyledTableContainer>
        <Table>
          <StyledTableHead
            sx={{
              backgroundColor: "#f0fafa",
              position: "sticky",
              top: 0,
              zIndex: 10,
            }}
          >
            <TableRow>
              <TableCell align="center">#</TableCell>
              <TableCell align="center">부서명</TableCell>
              <TableCell align="center">일련번호</TableCell>
              <TableCell align="center">내용</TableCell>
              <TableCell align="center">개정</TableCell>
              <TableCell align="center">편집</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {isLoading && isFetching ? (
              <MWSkeleton row={10} column={10} />
            ) : (
              evaluationList?.items.map((item, index) => (
                <TableRow key={item.uid}>
                  <TableCell align="center">
                    {page === 1
                      ? index + 1
                      : (page - 1) * initializeEvaluationListParams().rows! + index + 1}
                  </TableCell>
                  {!item.isOuter ? (
                    <TableCell align="center">
                      {departmentList?.items.map((value) =>
                        value.uid === item.departmentUID ? value.name : ""
                      )}
                    </TableCell>
                  ) : (
                    <TableCell align="center">--</TableCell>
                  )}

                  <TableCell align="center">{item.serialMessage}</TableCell>
                  <TableCell align="center">
                    {!item.isOuter ? (
                      <RouterLink to={`/Permission/RevisionList/${item.uid}?token=${tokenState}`}>
                        {item.contents}
                      </RouterLink>
                    ) : (
                      <>{item.contents}</>
                    )}
                  </TableCell>
                  <TableCell align="center">{item.revisionCount}</TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      disabled={item.revisionCount < 1}
                      style={{marginRight: "12px"}}
                    >
                      <Link
                        href={`/API/GetDangerousDocument?id=${item.uid}&token=${tokenState}`}
                        sx={{
                          display: "flex",
                          textDecoration: "none",
                          color: "white",
                        }}
                      >
                        <WordIcon width="20px" height="20px" />,
                        <span style={{paddingLeft: "4px", fontSize: "12px"}}>위험성평가</span>
                      </Link>
                    </Button>
                    <Button
                      variant="contained"
                      color="warning"
                      size="small"
                      disabled={item.revisionCount < 1}
                      style={{marginRight: "12px"}}
                    >
                      <Link
                        href={`/API/GetPermissionDocument?id=${item.uid}&token=${tokenState}`}
                        sx={{
                          display: "flex",
                          textDecoration: "none",
                          color: "white",
                        }}
                      >
                        <WordIcon width="20px" height="20px" />,
                        <span style={{paddingLeft: "4px", fontSize: "12px"}}>작업허가서</span>
                      </Link>
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{marginRight: "12px"}}
                      onClick={() => {
                        setAlertState({
                          toggle: true,
                          message: "해당 내용을 복사해서 가져오겠습니까?",
                          execution: () => copyMutate({token: tokenState, id: item.uid}),
                        });
                      }}
                    >
                      복사
                    </Button>
                    {item.departmentUID === userInfo.departmentUID && (
                      <React.Fragment>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          style={{marginRight: "12px"}}
                          onClick={() => {
                            navigate(`/Permission/EditMaster/${item.uid}`);
                          }}
                        >
                          편집
                        </Button>

                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => {
                            setAlertState({
                              toggle: true,
                              message: "등록된 리스트를 삭제하시겠습니까?",
                              execution: () => removeMutate({token: tokenState, id: item.uid}),
                            });
                          }}
                        >
                          삭제
                        </Button>
                      </React.Fragment>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* 페이지네이션 */}
      <PaginationContainer>
        <Pagination
          count={evaluationList?.maxPage}
          page={filter.page}
          onChange={paginationHandleChange}
          variant="outlined"
          color="primary"
          shape="rounded"
        />
      </PaginationContainer>
    </ListContainer>
  );
};

export default EvaluationMasterList;
